import React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard } from 'lucide-react';
import { formatPrice, convertFromEUR } from '../../utils/priceUtils';

interface CartDrawerSummaryProps {
    totalAmount: number;
    commissionPct: number;
    minFee: number;
    currency: string;
    exchangeRates?: Record<string, number>;
    onCheckout: () => void;
}

const CartDrawerSummary: React.FC<CartDrawerSummaryProps> = ({
    totalAmount,
    commissionPct,
    minFee,
    currency,
    exchangeRates = {},
    onCheckout
}) => {
    const { t } = useTranslation('marketplace');

    return (
        <div className="p-6 border-t bg-gray-50">
            <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 font-medium">{t('cart.subtotal')}</span>
                <span className="text-gray-900 font-bold">{formatPrice(totalAmount, currency)}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-400 italic">
                    {t('cart.service_fee', { percent: commissionPct })}
                </span>
                <span className="text-xs text-slate-400 italic">
                    {(() => {
                        const convertedMinFee = convertFromEUR(minFee, currency, exchangeRates);
                        return formatPrice(((totalAmount * (commissionPct / 100)) + (convertedMinFee * 100)), currency);
                    })()}
                </span>
            </div>
            <div className="flex items-center justify-between mb-6">
                <span className="text-gray-500 font-medium">{t('cart.shipping')}</span>
                <span className="text-green-600 font-bold text-sm">{t('cart.shipping_next_step')}</span>
            </div>
            <div className="flex items-center justify-between mb-6">
                <span className="text-gray-500 font-medium">{t('cart.promo_code')}</span>
                <span className="text-orange-500 font-bold text-sm">{t('catalog.discount_at_checkout')}</span>
            </div>
            <button
                onClick={onCheckout}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 rounded-2xl shadow-lg shadow-orange-200 transition-all flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-95"
            >
                <CreditCard className="w-5 h-5" />
                {t('cart.checkout_button')} • {(() => {
                    const convertedMinFee = convertFromEUR(minFee, currency, exchangeRates);
                    return formatPrice(((totalAmount + (totalAmount * (commissionPct / 100)) + (convertedMinFee * 100))), currency);
                })()}
            </button>
        </div>
    );
};

export default CartDrawerSummary;
